import React, { useEffect, useRef, useState } from 'react';
import {
    Box, TextField, Button, Card, CardContent, Typography, Select, MenuItem, FormControl,
    InputLabel, Checkbox, FormControlLabel, Alert, Grid
} from '@mui/material';
import { useJsApiLoader, Autocomplete } from '@react-google-maps/api';
import moment from 'moment';
import { serviceHTTP } from '../../util/service';
import { getAccessToken } from '../../util/customMethods';
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_LIBRARIES } from '../../util/googleMaps';

const emptyPickup = { addressLine: '', city: '', state: '', zip: '', landmark: '', latLong: '' };

const addressPart = (components, type, useShortName = false) => {
    const part = (components || []).find(c => c.types.includes(type));
    if (!part) return '';
    return useShortName ? part.short_name : part.long_name;
};

const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
});

const NewServiceRequest = () => {
    const [accounts, setAccounts] = useState([]);
    const [accountsLoaded, setAccountsLoaded] = useState(false);
    const [accountId, setAccountId] = useState('');
    const [pickup, setPickup] = useState(emptyPickup);
    const [vehicles, setVehicles] = useState(1);
    const [trucks, setTrucks] = useState(1);
    const [flatbed, setFlatbed] = useState(false);
    const [vin, setVin] = useState('');
    const [note, setNote] = useState('');
    const [photos, setPhotos] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [created, setCreated] = useState('');
    const autocompleteRef = useRef(null);
    const { isLoaded } = useJsApiLoader({ id: 'act-google-maps', googleMapsApiKey: GOOGLE_MAPS_API_KEY, libraries: GOOGLE_MAPS_LIBRARIES });

    useEffect(() => {
        serviceHTTP.get('serviceRequest/accounts', undefined, getAccessToken())
            .then(response => {
                const list = Array.isArray(response) ? response : [];
                setAccounts(list);
                if (list.length === 1) setAccountId(list[0].accountId);
            })
            .catch(() => setAccounts([]))
            .finally(() => setAccountsLoaded(true));
    }, []);

    const onPlaceChanged = () => {
        const place = autocompleteRef.current && autocompleteRef.current.getPlace();
        if (!place || !place.geometry) return;
        const components = place.address_components;
        const street = [addressPart(components, 'street_number'), addressPart(components, 'route')]
            .filter(Boolean).join(' ');
        setPickup({
            ...pickup,
            addressLine: street || place.name || '',
            city: addressPart(components, 'locality') || addressPart(components, 'sublocality'),
            state: addressPart(components, 'administrative_area_level_1', true),
            zip: addressPart(components, 'postal_code'),
            latLong: `${place.geometry.location.lat()},${place.geometry.location.lng()}`,
        });
    };

    const resetForm = () => {
        setPickup(emptyPickup);
        setVehicles(1);
        setTrucks(1);
        setFlatbed(false);
        setVin('');
        setNote('');
        setPhotos([]);
    };

    const handleSubmit = async () => {
        setError('');
        setCreated('');
        if (!accountId) {
            setError('Select an account.');
            return;
        }
        if (!pickup.latLong) {
            setError('Pick the pickup address from the suggestions so the location can be mapped.');
            return;
        }
        const vinValue = vin.trim().toUpperCase();
        if (vinValue && vinValue.length !== 17) {
            setError('VIN must be 17 characters, or leave it blank.');
            return;
        }
        setSubmitting(true);
        try {
            const now = moment();
            const body = {
                accountId,
                pickupAddrLine1: pickup.addressLine,
                pickupCity: pickup.city,
                pickupState: pickup.state,
                pickupZipcode: pickup.zip,
                pickupLandmark: pickup.landmark,
                pickupLatLong: pickup.latLong,
                pickupAddrVerified: 1,
                duplicateCopies: vehicles,
                noOfTrucks: trucks,
                flatbedNeeded: flatbed,
                VIN: vinValue ? [vinValue] : [],
                savedNote: note.trim(),
                pic: await Promise.all(photos.map(toBase64)),
                receiverDate: now.format('MM/DD/YYYY'),
                requestTime: now.format('MM/DD/YYYY HH:mm:ss'),
                receivedTime: now.format('MM/DD/YYYY HH:mm:ss'),
            };
            const response = await serviceHTTP.post('serviceRequest', body, undefined, getAccessToken());
            if (response.status === 200 && response.statusCode === 200) {
                setCreated(String(response.resp || ''));
                resetForm();
            } else {
                setError(response.message || 'Unable to submit the request. Please try again.');
            }
        } catch (e) {
            setError('Unable to submit the request. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (accountsLoaded && accounts.length === 0) {
        return (
            <Box>
                <Alert severity="info">None of your accounts are set up to take service requests. Contact All City Towing to enable it.</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 900 }}>
            {created && (
                <Alert severity="success" sx={{ marginBottom: 2 }}>
                    Request submitted. Invoice{created.includes(',') ? 's' : ''}: {created}
                </Alert>
            )}
            {error && <Alert severity="error" sx={{ marginBottom: 2 }}>{error}</Alert>}
            <Card variant="outlined">
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Account</InputLabel>
                                <Select label="Account" value={accountId} onChange={e => setAccountId(e.target.value)}>
                                    {accounts.map(a => (
                                        <MenuItem key={a.accountId} value={a.accountId}>{a.accountName}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            {isLoaded ? (
                                <Autocomplete
                                    onLoad={ref => { autocompleteRef.current = ref; }}
                                    onPlaceChanged={onPlaceChanged}
                                    options={{ componentRestrictions: { country: 'us' } }}
                                >
                                    <TextField
                                        fullWidth
                                        label="Pickup address"
                                        placeholder="Start typing and pick from the list"
                                        value={pickup.addressLine}
                                        onChange={e => setPickup({ ...pickup, addressLine: e.target.value, latLong: '' })}
                                    />
                                </Autocomplete>
                            ) : (
                                <TextField fullWidth label="Pickup address" disabled placeholder="Loading address search…" />
                            )}
                        </Grid>
                        <Grid item xs={12} sm={5}>
                            <TextField fullWidth label="City" value={pickup.city} InputProps={{ readOnly: true }} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField fullWidth label="State" value={pickup.state} InputProps={{ readOnly: true }} />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <TextField fullWidth label="Zip" value={pickup.zip} InputProps={{ readOnly: true }} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Landmark / location notes" value={pickup.landmark}
                                onChange={e => setPickup({ ...pickup, landmark: e.target.value })} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField fullWidth type="number" label="# of vehicles" value={vehicles}
                                inputProps={{ min: 1, max: 10 }}
                                onChange={e => setVehicles(Math.min(10, Math.max(1, parseInt(e.target.value, 10) || 1)))} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField fullWidth type="number" label="# of trucks" value={trucks}
                                inputProps={{ min: 1, max: 10 }}
                                onChange={e => setTrucks(Math.min(10, Math.max(1, parseInt(e.target.value, 10) || 1)))} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel control={<Checkbox checked={flatbed} onChange={e => setFlatbed(e.target.checked)} />}
                                label="Flatbed needed" />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="VIN (optional)" value={vin} inputProps={{ maxLength: 17 }}
                                onChange={e => setVin(e.target.value)} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth multiline minRows={3} label="Notes for dispatch" value={note}
                                inputProps={{ maxLength: 1000 }} onChange={e => setNote(e.target.value)} />
                        </Grid>
                        <Grid item xs={12}>
                            <Button variant="outlined" component="label">
                                Add vehicle photos
                                <input hidden type="file" accept="image/*" multiple
                                    onChange={e => setPhotos(Array.from(e.target.files || []))} />
                            </Button>
                            {photos.length > 0 && (
                                <Typography component="span" sx={{ marginLeft: 2 }}>
                                    {photos.length} photo{photos.length > 1 ? 's' : ''} selected
                                </Typography>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
                                {submitting ? 'Submitting...' : 'Submit Request'}
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </Box>
    );
};

export default NewServiceRequest;
